import React, { useState, useEffect } from 'react';
import { blogAPI } from '../../utils/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Loading from '../../components/common/Loading';
import ErrorMessage from '../../components/common/ErrorMessage';
import Modal from '../../components/common/Modal';
import BlogPostForm from '../../components/admin/BlogPostForm';
import { FaEdit, FaTrash, FaPlus, FaEye } from 'react-icons/fa';
import { formatCategory, formatDateShort } from '../../utils/formatters';

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await blogAPI.getAll();
      setPosts(response.data.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) {
      return;
    }

    try {
      await blogAPI.delete(id);
      setPosts(posts.filter((p) => p._id !== id));
    } catch (err) {
      alert(err.message || 'Failed to delete blog post');
    }
  };

  const handleOpenModal = (post = null) => {
    setCurrentPost(post);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentPost(null);
  };

  const handleSubmit = async (formData) => {
    try {
      setSubmitting(true);
      if (currentPost) {
        // Update existing post
        const response = await blogAPI.update(currentPost._id, formData);
        setPosts(
          posts.map((p) => (p._id === currentPost._id ? response.data.data : p))
        );
      } else {
        // Create new post
        const response = await blogAPI.create(formData);
        setPosts([response.data.data, ...posts]);
      }
      handleCloseModal();
    } catch (err) {
      alert(err.message || 'Failed to save blog post');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loading text="Loading blog posts..." />;
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold text-secondary mb-2">
            Manage Blog Posts
          </h1>
          <p className="text-text-primary font-body">
            Create, edit, and publish blog content
          </p>
        </div>
        <Button variant="primary" onClick={() => handleOpenModal()}>
          <FaPlus className="inline mr-2" />
          New Post
        </Button>
      </div>

      {/* Error Message */}
      {error && <ErrorMessage message={error} className="mb-6" />}

      {/* Blog Posts List */}
      {posts.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-text-primary font-body">
            No blog posts yet. Create your first post!
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {posts.map((post) => (
            <Card key={post._id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-xl font-heading font-semibold text-secondary">
                    {post.title}
                  </h3>
                  <div className="flex items-center gap-3 mt-1 text-sm text-gray-600 font-body">
                    {post.category && (
                      <span className="text-accent font-medium">
                        {formatCategory(post.category)}
                      </span>
                    )}
                    {post.publishedDate && (
                      <>
                        <span>•</span>
                        <span>{formatDateShort(post.publishedDate)}</span>
                      </>
                    )}
                    <span>•</span>
                    <span className={post.published ? 'text-green-600' : 'text-gray-500'}>
                      {post.published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  {post.excerpt && (
                    <p className="mt-2 text-text-primary font-body line-clamp-2">
                      {post.excerpt}
                    </p>
                  )}
                  {post.tags && post.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {post.tags.slice(0, 3).map((tag, idx) => (
                        <span
                          key={idx}
                          className="text-xs bg-gray-100 px-2 py-1 rounded font-body"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex space-x-2 ml-4">
                  <Button variant="outline" size="sm" title="Preview">
                    <FaEye />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    title="Edit"
                    onClick={() => handleOpenModal(post)}
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(post._id)}
                    className="text-red-600 hover:bg-red-50"
                    title="Delete"
                  >
                    <FaTrash />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal for Create/Edit */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={currentPost ? 'Edit Blog Post' : 'Create New Blog Post'}
        size="xlarge"
      >
        <BlogPostForm
          post={currentPost}
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          loading={submitting}
        />
      </Modal>
    </div>
  );
};

export default BlogManager;
